/* eslint no-underscore-dangle: ["error", { "allow": ["_id"] }] */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAdminApi from '../../service/adminService';
import useOrganizations from '../../service/useOrganizations';
import { Field, defaultInstructions, money } from './fields';
import EmailConfirmation, { emailsMatch } from './EmailConfirmation';
import {
    createEmptyOrderForm,
    hasValidAmountFormat,
    hasValidOrderFields,
} from './orderForm';

export default function NewOrder() {
    const navigate = useNavigate();
    const api = useAdminApi();
    const [form, setForm] = useState(createEmptyOrderForm);
    const {
        organizations, loading: organizationsLoading, error: organizationsError,
    } = useOrganizations(api);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [createReview, setCreateReview] = useState(false);
    const [customerEmailConfirmation, setCustomerEmailConfirmation] = useState('');
    const [billingEmailConfirmation, setBillingEmailConfirmation] = useState('');
    const savedBillingEmail = organizations.find((item) => item._id === form.organizationId)
        ?.billingEmail || '';
    const needsBillingConfirmation = !emailsMatch(form.billingEmail, savedBillingEmail);
    const emailsConfirmed = emailsMatch(form.customerEmail, customerEmailConfirmation)
        && (!needsBillingConfirmation || emailsMatch(form.billingEmail, billingEmailConfirmation));
    const canReview = hasValidOrderFields(form) && emailsConfirmed;

    useEffect(() => {
        setCreateReview(false);
    }, [form, customerEmailConfirmation, billingEmailConfirmation]);

    const selectOrganization = (event) => {
        const organization = organizations.find((item) => item._id === event.target.value);
        setBillingEmailConfirmation('');
        setForm((previous) => ({
            ...previous,
            organizationId: organization?._id || '',
            organizationName: organization?.name || '',
            billingContact: organization?.billingContact || '',
            billingEmail: organization?.billingEmail || '',
            billingAddress: organization?.billingAddress || '',
            paymentInstructions: organization?.paymentInstructions || defaultInstructions,
        }));
    };

    const review = (event) => {
        event.preventDefault();
        setError('');
        if (!form.organizationId) {
            setError('Select a purchasing organization first.');
            return;
        }
        if (!hasValidAmountFormat(form.amount)) {
            setError('Enter an amount with at most two decimal places.');
            return;
        }
        if (!emailsConfirmed) {
            setError('Confirm the email addresses before reviewing the order.');
            return;
        }
        setCreateReview(true);
    };

    const create = async () => {
        if (!canReview) return;
        setBusy(true);
        setError('');
        try {
            const data = await api('/orders', {
                method: 'POST',
                body: {
                    ...form,
                    amountCents: Math.round(Number(form.amount) * 100),
                    accessDays: Number(form.accessDays),
                },
            });
            setCreateReview(false);
            const notice = data.warnings.length
                ? `Order created. ${data.warnings.join(' ')}`
                : 'Order created. Both emails were sent.';
            navigate(`/admin/orders/${data.order._id}`, {
                replace: true,
                state: { notice },
            });
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            {(error || organizationsError) && (
                <p
                    className="po-error"
                    role="alert">
                    {error || organizationsError}
                </p>
            )}
            <div className="po-heading">
                <h2>New purchase order</h2>
            </div>
            <form
                onSubmit={review}
                className="po-panel po-form">
                <fieldset disabled={busy}>
                    <legend>Order setup</legend>
                    <div className="po-form-sections">
                        <section className="po-form-section">
                            <div className="po-section-heading">
                                <div>
                                    <h3>Purchasing organization</h3>
                                    <p>
                                        Select an organization to continue. Its billing
                                        defaults will fill in the order form.
                                    </p>
                                </div>
                            </div>
                            <label
                                htmlFor="ordereditor-organization"
                                className="po-field">
                                <span>Organization</span>
                                <select
                                    id="ordereditor-organization"
                                    value={form.organizationId}
                                    onChange={selectOrganization}
                                    disabled={organizationsLoading}
                                    required>
                                    <option value="">
                                        {organizationsLoading
                                            ? 'Loading organizations…'
                                            : 'Select an organization'}
                                    </option>
                                    {form.organizationId
                                        && !organizations.some((item) => (
                                            item._id === form.organizationId
                                        )) && (
                                        <option value={form.organizationId}>
                                            {form.organizationName}
                                        </option>
                                    )}
                                    {organizations.map((organization) => (
                                        <option
                                            key={organization._id}
                                            value={organization._id}>
                                            {organization.name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            {!organizationsLoading && !organizations.length && (
                                <p className="po-notice">
                                    Add a purchasing organization from the Purchasing
                                    organizations tab before creating an order.
                                </p>
                            )}
                        </section>

                        {form.organizationId && (
                            <>
                                <section className="po-form-section">
                                    <div className="po-section-heading">
                                        <div>
                                            <h3>Purchase details</h3>
                                            <p>
                                                Enter the PO reference, product, amount,
                                                and access term.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="po-grid">
                                        <Field
                                            label="PO number"
                                            name="poNumber"
                                            values={form}
                                            setValues={setForm}
                                            maxLength={100}
                                            required />
                                        <Field
                                            label="Product"
                                            name="product"
                                            values={form}
                                            setValues={setForm}
                                            maxLength={200}
                                            required />
                                        <Field
                                            label="Amount (USD)"
                                            name="amount"
                                            values={form}
                                            setValues={setForm}
                                            type="number"
                                            min="0.01"
                                            max="1000000"
                                            step="0.01"
                                            required />
                                        <Field
                                            label="Access duration (days)"
                                            name="accessDays"
                                            values={form}
                                            setValues={setForm}
                                            type="number"
                                            min="1"
                                            max="3650"
                                            step="1"
                                            required />
                                        <Field
                                            label="Payment due date (optional)"
                                            name="dueDate"
                                            values={form}
                                            setValues={setForm}
                                            type="date" />
                                    </div>
                                </section>

                                <section className="po-form-section">
                                    <div className="po-section-heading">
                                        <div>
                                            <h3>Access recipient</h3>
                                            <p>
                                                This person receives the order confirmation and
                                                access code.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="po-grid">
                                        <Field
                                            label="Recipient name"
                                            name="customerName"
                                            values={form}
                                            setValues={setForm}
                                            maxLength={160}
                                            required />
                                        <Field
                                            label="Recipient email"
                                            name="customerEmail"
                                            values={form}
                                            setValues={setForm}
                                            type="email"
                                            maxLength={254}
                                            required />
                                        <Field
                                            label="Student / family reference (optional)"
                                            name="familyReference"
                                            values={form}
                                            setValues={setForm}
                                            maxLength={160} />
                                        <EmailConfirmation
                                            label="Confirm recipient email"
                                            email={form.customerEmail}
                                            value={customerEmailConfirmation}
                                            onChange={setCustomerEmailConfirmation} />
                                    </div>
                                </section>

                                <section className="po-form-section">
                                    <div className="po-section-heading">
                                        <div>
                                            <h3>Billing and payment</h3>
                                            <p>The invoice is sent to the email shown here.</p>
                                        </div>
                                    </div>
                                    <div className="po-grid">
                                        <Field
                                            label="Billing contact"
                                            name="billingContact"
                                            values={form}
                                            setValues={setForm}
                                            maxLength={200} />
                                        <Field
                                            label="Invoice recipient email"
                                            name="billingEmail"
                                            values={form}
                                            setValues={setForm}
                                            type="email"
                                            maxLength={254}
                                            required />
                                        <Field
                                            label="Billing address"
                                            name="billingAddress"
                                            values={form}
                                            setValues={setForm}
                                            maxLength={600}
                                            multiline />
                                        {needsBillingConfirmation && (
                                            <EmailConfirmation
                                                label="Confirm invoice recipient email"
                                                email={form.billingEmail}
                                                value={billingEmailConfirmation}
                                                onChange={setBillingEmailConfirmation} />
                                        )}
                                        <Field
                                            label="Payment instructions"
                                            name="paymentInstructions"
                                            values={form}
                                            setValues={setForm}
                                            maxLength={1500}
                                            required
                                            multiline />
                                    </div>
                                </section>

                                <section className="po-form-section po-form-section-compact">
                                    <div className="po-section-heading">
                                        <div>
                                            <h3>Internal notes</h3>
                                            <p>
                                                These notes stay in the admin record and
                                                are not emailed.
                                            </p>
                                        </div>
                                    </div>
                                    <Field
                                        label="Notes (optional)"
                                        name="notes"
                                        values={form}
                                        setValues={setForm}
                                        maxLength={2000}
                                        multiline />
                                </section>
                            </>
                        )}
                    </div>
                    {form.organizationId && (
                        <div className="po-form-footer">
                            <button
                                type="submit"
                                disabled={!canReview || organizationsLoading}>
                                Review & create order
                            </button>
                            <p>
                                You will review the recipients and amount before the access
                                code is generated and emails are sent.
                            </p>
                        </div>
                    )}
                    {form.organizationId && createReview && (
                        <div className="po-review">
                            <h4>Confirm order and recipients</h4>
                            <p>
                                Create this order, send access to
                                {' '}
                                <strong>{form.customerEmail}</strong>
                                {' '}
                                and send the invoice to
                                {' '}
                                <strong>{form.billingEmail}</strong>
                                {' '}
                                {`for ${money(Math.round(Number(form.amount) * 100))}`}
                                . This immediately generates a real access code and
                                sends both emails.
                            </p>
                            <div className="po-actions">
                                <button
                                    type="button"
                                    disabled={busy || !canReview}
                                    onClick={create}>
                                    {busy ? 'Creating…' : 'Confirm & create order'}
                                </button>
                                <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => setCreateReview(false)}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </fieldset>
            </form>
        </div>
    );
}
